import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import {
  getSessionDrawerChrome,
  sessionDrawerCompareAddButtonSx,
  sessionDrawerHeaderSx,
  sessionDrawerIconButtonSx,
  sessionDrawerPaperSx,
  sessionDrawerResetButtonSx,
} from '@kangaroo/ui';
import type { TelemetrySessionContext } from '@kangaroo/shared';
import type { SessionLapTimingOverlay, SessionLapTimingWindow } from '@kangaroo/shared';

import type { CompareSlotState } from '../../features/sessions/compareSlots';
import type { LapTimingMode } from '../../features/sessions/useSessionLapSlot';
import type { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import type { CompareSessionGroups, SessionListItem } from '../../features/sessions/sessionList';
import type { MyChronSessionExport } from '../../lib/telemetrySession';
import { TelemetryDrawerSection } from '../telemetry/TelemetryDrawerSection';
import { TelemetrySessionContextPanel } from '../telemetry/TelemetrySessionContextPanel';
import { SessionCompareSlotPanel } from './SessionCompareSlotPanel';
import { SessionLapPanel } from './SessionLapPanel';
import { SessionSelectionTree } from './SessionSelectionTree';
import { SessionsLapTimingControls } from './SessionsLapTimingControls';
import {
  listSessionFootageUploadsPage,
  type GoProUploadSessionListItem,
  type GoProUploadSessionPage,
} from '../../lib/goproResumableUpload';

const EMPTY_SESSION_FOOTAGE: GoProUploadSessionListItem[] = [];

function lapUtcMs(lap: SessionLapTimingWindow, field: 'utcStartIso' | 'utcEndIso') {
  const value = lap[field];
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function footageUtcMs(upload: GoProUploadSessionListItem, field: 'startedAtIso' | 'endedAtIso') {
  const value = upload[field];
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function footageStartConfirmed(upload: GoProUploadSessionListItem) {
  return Boolean(upload.footageStartConfirmedAt)
    || upload.footageTimingSource === 'manual-map'
    || upload.footageTimingSource === 'gopro-telemetry';
}

function footageEndUtcMs(upload: GoProUploadSessionListItem, footageStartMs: number) {
  const explicitEndMs = footageUtcMs(upload, 'endedAtIso');
  if (explicitEndMs != null) {
    return explicitEndMs;
  }

  const durationSec = upload.videoDurationSec;
  return typeof durationSec === 'number' && Number.isFinite(durationSec) && durationSec > 0
    ? footageStartMs + durationSec * 1000
    : null;
}

export function buildFootageCountsByLap(
  overlay: SessionLapTimingOverlay | null | undefined,
  mode: LapTimingMode,
  uploads: GoProUploadSessionListItem[],
) {
  const laps =
    mode === 'circuit'
      ? overlay?.circuitLapSet?.laps ?? []
      : overlay?.rawLaps ?? [];
  const counts: Record<number, number> = {};

  for (const lap of laps) {
    const lapStartMs = lapUtcMs(lap, 'utcStartIso');
    const lapEndMs = lapUtcMs(lap, 'utcEndIso');
    if (lapStartMs == null || lapEndMs == null || lapEndMs <= lapStartMs) {
      continue;
    }

    const count = uploads.reduce((total, upload) => {
      if (!footageStartConfirmed(upload)) {
        return total;
      }

      const footageStartMs = footageUtcMs(upload, 'startedAtIso');
      const footageEndMs = footageStartMs == null ? null : footageEndUtcMs(upload, footageStartMs);
      if (footageStartMs == null || footageEndMs == null || footageEndMs <= footageStartMs) {
        return total;
      }

      return Math.min(lapEndMs, footageEndMs) > Math.max(lapStartMs, footageStartMs)
        ? total + 1
        : total;
    }, 0);

    if (count > 0) {
      counts[lap.lapNumber] = count;
    }
  }

  return counts;
}

type SessionSelectorDrawerProps = {
  open: boolean;
  publicDemo?: boolean;
  onClose: () => void;
  sessions: SessionListItem[];
  listLoading?: boolean;
  error?: string | null;
  hasMoreSessions?: boolean;
  loadingMoreSessions?: boolean;
  loadMoreError?: string | null;
  sessionListSummary?: string | null;
  onLoadMoreSessions?: () => void;
  onRetrySessionList?: () => void;
  primarySessionId: string | null;
  primarySessionExport: MyChronSessionExport | null | undefined;
  primaryLapNumber: number | null;
  lapTimingMode: LapTimingMode;
  onLapTimingModeChange: (mode: LapTimingMode) => void;
  primarySlot: ReturnType<typeof useSessionLapSlot>;
  onSessionSelectionChange: (sessionIds: string[]) => void;
  onPrimaryLapChange: (lapNumber: number) => void;
  compareSlots: CompareSlotState[];
  onAddCompareSlot: () => void;
  onRemoveCompareSlot: (slotId: string) => void;
  onCompareSlotSessionChange: (slotId: string, sessionId: string) => void;
  onCompareSlotLapChange: (slotId: string, lapNumber: number) => void;
  compareSessionGroups: CompareSessionGroups;
  onReset: () => void;
  onSessionContextSaved: (
    sessionKey: string,
    context: TelemetrySessionContext,
    driverName: string | null,
  ) => void;
};

export function SessionSelectorDrawer({
  open,
  publicDemo = false,
  onClose,
  sessions,
  listLoading = false,
  error = null,
  hasMoreSessions = false,
  loadingMoreSessions = false,
  loadMoreError = null,
  sessionListSummary = null,
  onLoadMoreSessions,
  onRetrySessionList,
  primarySessionId,
  primarySessionExport,
  primaryLapNumber,
  lapTimingMode,
  onLapTimingModeChange,
  primarySlot,
  onSessionSelectionChange,
  onPrimaryLapChange,
  compareSlots,
  onAddCompareSlot,
  onRemoveCompareSlot,
  onCompareSlotSessionChange,
  onCompareSlotLapChange,
  compareSessionGroups,
  onReset,
  onSessionContextSaved,
}: SessionSelectorDrawerProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const primarySelectedCircuitLap =
    lapTimingMode === 'circuit' ? primarySlot.selectedLapNumber : null;
  const {
    data: primaryFootagePages,
    error: primaryFootageError,
    isLoading: primaryFootageLoading,
    isValidating: primaryFootageValidating,
    size: primaryFootagePageCount,
    setSize: setPrimaryFootagePageCount,
    mutate: retryPrimaryFootage,
  } = useSWRInfinite<GoProUploadSessionPage>(
    (_pageIndex, previousPage) => {
      if (!open || !primarySessionId || publicDemo || (previousPage && !previousPage.hasMore)) {
        return null;
      }
      return ['session-selector-footage', primarySessionId, previousPage?.nextCursor ?? null] as const;
    },
    ([, activeSessionId, cursor]) => listSessionFootageUploadsPage(String(activeSessionId), {
      cursor: typeof cursor === 'string' ? cursor : null,
    }),
    { revalidateOnFocus: false },
  );
  const primaryFootage = useMemo(
    () => primaryFootagePages?.flatMap((page) => page.items) ?? EMPTY_SESSION_FOOTAGE,
    [primaryFootagePages],
  );
  const primaryFootageHasMore = primaryFootagePages?.at(-1)?.hasMore ?? false;
  const primaryFootageHasDuplicates = useMemo(() => (
    new Set(primaryFootage.map((upload) => upload.uploadSessionId)).size !== primaryFootage.length
  ), [primaryFootage]);
  const primaryFootageFailure = primaryFootageError
    ?? (primaryFootageHasDuplicates
      ? new Error('Session footage changed while loading. Retry to refresh the complete list.')
      : null);
  const primaryFootageCountsByLap = useMemo(() => (
    buildFootageCountsByLap(
      primarySlot.sessionContext?.lapTimingOverlay,
      lapTimingMode,
      primaryFootage,
    )
  ), [lapTimingMode, primaryFootage, primarySlot.sessionContext?.lapTimingOverlay]);

  useEffect(() => {
    if (
      open
      && hasMoreSessions
      && !listLoading
      && !loadingMoreSessions
      && !loadMoreError
      && onLoadMoreSessions
    ) {
      onLoadMoreSessions();
    }
  }, [hasMoreSessions, listLoading, loadMoreError, loadingMoreSessions, onLoadMoreSessions, open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        disableEnforceFocus: true,
        disableScrollLock: true,
      }}
      PaperProps={{
        sx: sessionDrawerPaperSx(isDark),
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={sessionDrawerHeaderSx(isDark)}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: chrome.title }}>
            Session & laps
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Button size="small" onClick={onReset} sx={sessionDrawerResetButtonSx(isDark)}>
              Reset
            </Button>
            <IconButton
              aria-label="Close session selector"
              onClick={onClose}
              size="small"
              sx={sessionDrawerIconButtonSx(isDark)}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1.5 }}>
          {open ? (
          listLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Stack spacing={1.25} alignItems="center" sx={{ py: 3 }}>
              <Typography sx={{ fontSize: '0.78rem', color: chrome.error, textAlign: 'center' }}>
                {error}
              </Typography>
              {onRetrySessionList ? <Button size="small" variant="outlined" onClick={onRetrySessionList}>Retry session list</Button> : null}
            </Stack>
          ) : sessions.length === 0 ? (
            <Typography sx={{ fontSize: '0.78rem', color: chrome.empty, textAlign: 'center', py: 3 }}>
              No sessions uploaded yet.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ position: 'relative', isolation: 'isolate' }}>
              <Stack spacing={0.75}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: chrome.sectionLabel, letterSpacing: '0.04em' }}>
                  SESSIONS
                </Typography>
                <Typography sx={{ fontSize: '0.69rem', color: chrome.muted, lineHeight: 1.45 }}>
                  Open a date and driver, then check more sessions to compare. Select a session name to make it primary.
                </Typography>
                <SessionSelectionTree
                  sessions={sessions}
                  primarySessionId={primarySessionId}
                  compareSessionIds={compareSlots.map((slot) => slot.sessionId)}
                  onSelectionChange={onSessionSelectionChange}
                />
              </Stack>

              {!publicDemo && primarySessionId ? (
                <Stack spacing={0.75} alignItems="stretch">
                  {primaryFootageLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center" role="status">
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: '0.72rem', color: chrome.muted }}>
                        Loading session footage…
                      </Typography>
                    </Stack>
                  ) : primaryFootageFailure ? (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography role="alert" sx={{ fontSize: '0.72rem', color: chrome.error }}>
                        Session footage is incomplete.
                      </Typography>
                      <Button size="small" onClick={() => void retryPrimaryFootage()}>
                        Retry
                      </Button>
                    </Stack>
                  ) : primaryFootageHasMore ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={primaryFootageValidating}
                      onClick={() => void setPrimaryFootagePageCount(primaryFootagePageCount + 1)}
                    >
                      {primaryFootageValidating ? 'Loading older footage…' : 'Load older session footage'}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}

              {hasMoreSessions || loadMoreError ? (
                <Stack spacing={0.75} alignItems="stretch">
                  {sessionListSummary ? (
                    <Typography sx={{ fontSize: '0.72rem', color: chrome.muted, textAlign: 'center' }}>
                      {sessionListSummary}
                    </Typography>
                  ) : null}
                  {loadMoreError ? (
                    <Typography sx={{ fontSize: '0.72rem', color: chrome.error, textAlign: 'center' }}>
                      {loadMoreError}
                    </Typography>
                  ) : null}
                  {loadMoreError && onRetrySessionList ? (
                    <Button size="small" variant="outlined" onClick={onRetrySessionList}>Reload session list</Button>
                  ) : hasMoreSessions && onLoadMoreSessions ? (
                    <Button size="small" variant="outlined" disabled={loadingMoreSessions} onClick={onLoadMoreSessions} startIcon={loadingMoreSessions ? <CircularProgress size={14} /> : undefined}>
                      {loadingMoreSessions ? 'Loading more sessions…' : 'Load more sessions'}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}

              {!publicDemo || primarySlot.hasCircuitOverlay ? (
                <SessionsLapTimingControls
                  lapTimingMode={lapTimingMode}
                  onLapTimingModeChange={onLapTimingModeChange}
                  setupSlot={primarySlot}
                  setupDialogTitle="Primary session · circuit setup"
                />
              ) : null}

              {!publicDemo && primarySessionId && primarySessionExport ? (
                <TelemetryDrawerSection
                  title="Assignments"
                  description="Driver and vehicle for this session."
                  defaultExpanded={false}
                >
                  <TelemetrySessionContextPanel
                    sessionKey={primarySessionId}
                    session={primarySessionExport}
                    sessionContext={primarySlot.sessionContext ?? null}
                    dataFile={null}
                    canEdit={primarySlot.canEdit}
                    variant="drawer"
                    hideCircuitAssignment
                    showVehicleSetup={false}
                    onSaved={({ context, driverName }) => {
                      onSessionContextSaved(primarySessionId, context, driverName);
                    }}
                  />
                </TelemetryDrawerSection>
              ) : null}

              <SessionLapPanel
                heading="PRIMARY LAP"
                sessionId={primarySessionId}
                lapTimingMode={lapTimingMode}
                slot={primarySlot}
                mychronLapNumber={primaryLapNumber}
                onMychronLapChange={onPrimaryLapChange}
                showSessionSelect={false}
                selectionMode="primary"
                footageCountsByLap={primaryFootageCountsByLap}
              />

              <Divider sx={{ borderColor: chrome.divider }} />

              <Stack spacing={1.25}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: chrome.sectionLabel, letterSpacing: '0.04em' }}>
                  COMPARE LAPS
                </Typography>

                {primarySessionId ? (
                  <Button
                    aria-label="Compare another lap from the primary session"
                    size="small"
                    variant="outlined"
                    onClick={onAddCompareSlot}
                    startIcon={<AddRoundedIcon sx={{ fontSize: 20 }} />}
                    fullWidth
                    sx={{
                      ...sessionDrawerCompareAddButtonSx(isDark, true),
                      '& .MuiButton-startIcon': { alignSelf: 'center', mr: 1 },
                    }}
                  >
                    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                      <Typography sx={{ fontSize: '0.77rem', fontWeight: 850, lineHeight: 1.25 }}>
                        Compare another lap
                      </Typography>
                      <Typography component="span" sx={{ display: 'block', mt: 0.15, fontSize: '0.64rem', fontWeight: 550, color: chrome.muted, lineHeight: 1.3 }}>
                        Uses the primary session
                      </Typography>
                    </Box>
                  </Button>
                ) : null}

                {compareSlots.map((slot, index) => (
                  <SessionCompareSlotPanel
                    key={slot.id}
                    publicDemo={publicDemo}
                    index={index}
                    sessionId={slot.sessionId}
                    lapNumber={slot.lapNumber}
                    lapTimingMode={lapTimingMode}
                    sessionGroups={compareSessionGroups}
                    sessions={sessions}
                    primarySessionId={primarySessionId}
                    primarySessionExport={primarySessionExport}
                    primaryLapNumber={primaryLapNumber}
                    primarySelectedCircuitLap={primarySelectedCircuitLap}
                    primaryCircuitOverlay={primarySlot.resolvedCircuit.overlay}
                    onSessionChange={(sessionId) => onCompareSlotSessionChange(slot.id, sessionId)}
                    onLapChange={(lapNumber) => onCompareSlotLapChange(slot.id, lapNumber)}
                    onRemove={() => onRemoveCompareSlot(slot.id)}
                  />
                ))}

                {compareSlots.length === 0 ? (
                  <Typography sx={{ fontSize: '0.68rem', color: chrome.muted, lineHeight: 1.45, px: 0.25 }}>
                    To compare a different session, check it in the folder list above.
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          )) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
